import React, { useState, useEffect } from 'react';
import { ArrowLeft, Heart, Shield, Flag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ReligionType, ReligionIntent } from '@/hooks/useReligionSettings';
import { useReligionDiscovery, ReligionProfile } from '@/hooks/useReligionDiscovery';
import ProfileBalloon from '@/components/pop-balloon/ProfileBalloon';
import ActionBar from '@/components/pop-balloon/ActionBar';
import PhotosModal from '@/components/pop-balloon/PhotosModal';
import ProfileModal from '@/components/pop-balloon/ProfileModal';
import VideoModal from '@/components/pop-balloon/VideoModal';
import MatchModal from '@/components/pop-balloon/MatchModal';
import ReligionReportModal from './ReligionReportModal';

interface ReligionDiscoveryScreenProps {
  religion: ReligionType;
  intent: ReligionIntent;
  waliNote?: string;
  onBack: () => void;
}

const ReligionDiscoveryScreen: React.FC<ReligionDiscoveryScreenProps> = ({
  religion,
  intent,
  waliNote,
  onBack
}) => {
  const { profiles, loading, fetchProfiles, likeProfile, popProfile, reportProfile } = useReligionDiscovery(religion, intent);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPhotos, setShowPhotos] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [matchedProfile, setMatchedProfile] = useState<ReligionProfile | null>(null);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const currentProfile = profiles[currentIndex];

  const getReligionIcon = () => {
    switch (religion) {
      case 'jewish': return '✡️';
      case 'christian': return '✝️';
      case 'muslim': return '☪️';
      case 'hindu': return '🕉️';
      default: return '🙏';
    }
  };

  const getReligionName = () => {
    switch (religion) {
      case 'jewish': return 'Jewish';
      case 'christian': return 'Christian';
      case 'muslim': return 'Muslim';
      case 'hindu': return 'Hindu';
      default: return 'Faith';
    }
  };

  const getIntentLabel = () => {
    switch (intent) {
      case 'marriage': return 'Marriage';
      case 'serious': return 'Serious';
      case 'exploring': return 'Exploring';
    }
  };

  const goToNext = () => {
    if (currentIndex < profiles.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      toast.info('You\'ve seen all profiles in this faith space');
    }
  };

  const handleLike = async () => {
    if (!currentProfile) return;
    await likeProfile(currentProfile.id);
    toast.success('Like sent');
    goToNext();
  };

  const handlePop = async () => {
    if (!currentProfile) return;

    // For Muslim marriage mode, require video
    if (religion === 'muslim' && intent === 'marriage' && !currentProfile.has_intro_video) {
      toast.error('Video required to pop in Marriage mode');
      return;
    }

    const { isMatch } = await popProfile(currentProfile.id);
    
    if (isMatch) {
      setMatchedProfile(currentProfile);
      setShowMatch(true);
    } else {
      toast.success('Pop sent! 🎈');
      goToNext();
    }
  };

  const handleReport = async (reason: string, details?: string) => {
    if (!currentProfile) return;
    await reportProfile(
      currentProfile.id,
      reason as 'disrespectful_behavior' | 'faith_misrepresentation' | 'intent_misrepresentation' | 'harassment' | 'inappropriate_content',
      details
    );
    toast.success('Report submitted');
    setShowReport(false);
    goToNext();
  };

  // Adapt profile for existing components
  const adaptedProfile = currentProfile ? {
    id: currentProfile.id,
    name: currentProfile.name,
    age: currentProfile.age || 0,
    bio: currentProfile.bio || '',
    photos: currentProfile.photos.length > 0 ? currentProfile.photos : [currentProfile.photo_url || '/placeholder.svg'],
    isVerified: currentProfile.is_verified,
    hasVideo: currentProfile.has_intro_video,
    videoUrl: currentProfile.video_url || undefined,
    distance: 'Faith Match',
    badges: [currentProfile.intent || 'exploring']
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Finding faithful connections...</p>
        </div>
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{getReligionIcon()}</span>
            <span className="font-semibold">{getReligionName()}</span>
          </div>
          <div className="w-10" />
        </div>

        <div className="flex flex-col items-center justify-center flex-1 mt-20">
          <div className="text-6xl mb-4">🙏</div>
          <h2 className="text-xl font-bold text-foreground mb-2">No profiles yet</h2>
          <p className="text-muted-foreground text-center max-w-xs mb-6">
            Be patient - more faithful connections will appear soon
          </p>
          <Button onClick={onBack} variant="outline" className="rounded-full">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/80 via-orange-50/80 to-rose-50/80 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 relative overflow-hidden pb-24">
      {/* Header */}
      <div className="relative z-20 flex items-center justify-between p-4">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        <div className="flex items-center gap-2">
          <span className="text-xl">{getReligionIcon()}</span>
          <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            {getReligionName()} • {getIntentLabel()}
          </Badge>
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowReport(true)}
          className="rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm"
        >
          <Flag className="w-5 h-5 text-muted-foreground" />
        </Button>
      </div>

      {/* Disclaimer Banner */}
      <div className="mx-4 mb-4 p-3 rounded-xl bg-amber-100/80 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-600" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            This space is for respectful, faith-aligned connections only.
          </p>
        </div>
      </div>

      {/* Intent Badge on Profile */}
      {currentProfile.intent && (
        <div className="flex justify-center mb-2">
          <Badge 
            variant="outline" 
            className={`${
              currentProfile.intent === 'marriage' 
                ? 'bg-rose-100 border-rose-300 text-rose-700' 
                : 'bg-blue-100 border-blue-300 text-blue-700'
            }`}
          >
            {currentProfile.intent === 'marriage' && <Heart className="w-3 h-3 mr-1" />}
            {currentProfile.intent.charAt(0).toUpperCase() + currentProfile.intent.slice(1)}
          </Badge>
        </div>
      )}

      {/* Wali Note if applicable */}
      {currentProfile.wali_note && religion === 'muslim' && intent === 'marriage' && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <p className="text-xs text-green-700 dark:text-green-300">
            <strong>Wali Note:</strong> {currentProfile.wali_note}
          </p>
        </div>
      )}

      {/* Profile Balloon */}
      <div className="flex-1 flex items-center justify-center px-4">
        {adaptedProfile && (
          <ProfileBalloon
            profile={adaptedProfile}
            onViewPhotos={() => setShowPhotos(true)}
            onViewProfile={() => setShowProfile(true)}
            onWatchVideo={() => currentProfile.has_intro_video && setShowVideo(true)}
          />
        )}
      </div>

      {/* Action Bar */}
      <div className="fixed bottom-20 left-0 right-0 z-30">
        <ActionBar
          onLike={handleLike}
          onPop={handlePop}
          onWatchVideo={() => currentProfile.has_intro_video && setShowVideo(true)}
        />
      </div>

      {/* Modals */}
      {adaptedProfile && (
        <>
          <PhotosModal
            isOpen={showPhotos}
            onClose={() => setShowPhotos(false)}
            profile={adaptedProfile}
          />

          <ProfileModal
            isOpen={showProfile}
            onClose={() => setShowProfile(false)}
            profile={adaptedProfile}
            onReport={() => setShowReport(true)}
            onBlock={() => {
              // Block is handled via report modal in religion context
              setShowReport(true);
            }}
          />

          {adaptedProfile.videoUrl && (
            <VideoModal
              isOpen={showVideo}
              onClose={() => setShowVideo(false)}
              profile={adaptedProfile}
            />
          )}

          {matchedProfile && (
            <MatchModal
              isOpen={showMatch}
              onClose={() => {
                setShowMatch(false);
                goToNext();
              }}
              profile={adaptedProfile}
              onStartChat={() => {
                setShowMatch(false);
              }}
              onViewProfile={() => setShowProfile(true)}
            />
          )}

          <ReligionReportModal
            isOpen={showReport}
            onClose={() => setShowReport(false)}
            onReport={handleReport}
            profileName={currentProfile.name}
          />
        </>
      )}
    </div>
  );
};

export default ReligionDiscoveryScreen;
